import { useQuery } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';

import { authConfigQueries } from '../../../api/authConfigQueries';
import { KeycloakAuthProvider } from '../methods/keycloak/KeycloakAuthProvider';
import { SimulatedAuthProvider } from '../methods/simulated/SimulatedAuthProvider';
import {
  apiBaseUrl,
  mockApi,
  oidcAuthority,
  oidcClientId,
} from '../../../runtimeConfig';

const envAuthority = () => oidcAuthority()?.trim();

/* the client id belongs to this frontend and stays env-configured;
   mounted without one, the OIDC library throws only when the login
   button is clicked — a button that visibly does nothing */
const clientId = () => oidcClientId()?.trim();

/* without a usable answer there is no login to offer: a substitute
   would only collect 401s later, so the page names the cause instead */
const AuthConfigError = ({
  cause,
  onRetry,
}: {
  cause: string;
  onRetry?: () => void;
}) => {
  const { t } = useTranslation();
  return (
    <div className="auth-error">
      <h2>{t('auth.errorTitle')}</h2>
      <pre>{cause}</pre>
      {/* a build-time cause offers no retry — only a new build helps */}
      {onRetry && <button onClick={onRetry}>{t('error.retry')}</button>}
    </div>
  );
};

const MissingClientIdError = () => {
  const { t } = useTranslation();
  return <AuthConfigError cause={t('auth.configMissingClientId')} />;
};

/*
 * The OIDC provider creates its UserManager once, on first render —
 * so the answer must be awaited here; it cannot be swapped in later.
 */
const BackendConfiguredAuth = ({ children }: { children: React.ReactNode }) => {
  const { t } = useTranslation();
  const authConfig = useQuery(authConfigQueries.config());

  if (authConfig.isPending) return <div>{t('common.loading')}</div>;

  const { method, authority } = authConfig.data ?? {};
  if (method === 'oidc' && authority) {
    if (!clientId()) return <MissingClientIdError />;
    return (
      <KeycloakAuthProvider authority={authority}>
        {children}
      </KeycloakAuthProvider>
    );
  }

  /* only as the backend's explicit choice — no longer a fallback */
  if (method === 'simulated') {
    return <SimulatedAuthProvider>{children}</SimulatedAuthProvider>;
  }

  const isUnknownMethod =
    Boolean(method) && method !== 'oidc' && method !== 'simulated';
  return (
    <AuthConfigError
      cause={
        isUnknownMethod
          ? t('auth.configUnknownMethod', { method })
          : t('auth.configUnavailable', {
              url: apiBaseUrl() || '?',
            })
      }
      onRetry={() => authConfig.refetch()}
    />
  );
};

export const AuthProvider = ({ children }: { children: React.ReactNode }) => {
  /* a configured authority is pinned; only an empty one delegates the
     choice to the backend */
  const pinnedAuthority = envAuthority();
  if (pinnedAuthority) {
    if (!clientId()) return <MissingClientIdError />;
    return (
      <KeycloakAuthProvider authority={pinnedAuthority}>
        {children}
      </KeycloakAuthProvider>
    );
  }

  /* the mock has no backend to ask; its simulated login is the
     deliberate choice there, not a fallback */
  if (mockApi()) {
    return <SimulatedAuthProvider>{children}</SimulatedAuthProvider>;
  }

  return <BackendConfiguredAuth>{children}</BackendConfiguredAuth>;
};
